import {
  cloneElement,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset as floatingOffset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  type Placement,
  type UseHoverProps,
} from "@floating-ui/react";

import { Icon } from "@/components/icons/Icon";
import { type DropdownItem } from "@/components/sidebar/Dropdown";
import { twMerge } from "@/utils/twMerge";
import { useRovingNav } from "@/utils/useRovingNav";

type ChildrenArgs = {
  open: boolean;
  close: () => void;
};

function isInsideNestedFloating(target: Element) {
  return !!target.closest('[role="menu"], [role="listbox"], [role="dialog"]');
}

function hasOpenNestedFloating(panel: Element | null) {
  return !!panel?.querySelector('[aria-expanded="true"]');
}

function hasFocusedChild(container: Element | null) {
  const active = document.activeElement;
  return !!container && !!active && container.contains(active);
}

export type HoverPopoverProps = {
  /** The button that opens the popover; it gets the popover's expanded state */
  toggleButtonComponent: React.ReactNode;
  /** Names the popover for screen readers */
  label?: string;
  /** `radio`: `items` are a single choice, the active one checked */
  itemsRole?: "radio";
  className?: string;
  itemsClassName?: string;
  placement?: Placement;
  offset?: number;
  delay?: UseHoverProps["delay"];
  items?: DropdownItem[];
  children?: React.ReactNode | ((args: ChildrenArgs) => React.ReactNode);
};

export function HoverPopover({
  toggleButtonComponent,
  label,
  itemsRole,
  className,
  itemsClassName,
  placement,
  offset,
  delay = { open: 100, close: 200 },
  items,
  children,
}: HoverPopoverProps) {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [floatingOffset(offset), flip(), shift({ padding: 8 })],
  });

  const hover = useHover(context, { delay, enabled: !open });
  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      const target = event.target as Element | null;
      if (!target) return true;
      return !isInsideNestedFloating(target);
    },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, click, dismiss]);

  const close = useCallback(() => setOpen(false), []);
  // Focus moves into the popover only when the keyboard opened it; hovering must not take focus
  const openedWithKeyboardRef = useRef(false);

  // The items list one under another: one Tab stop, Up/Down between them, a radio list on its choice
  const itemsRef = useRef<HTMLDivElement>(null);
  useRovingNav(itemsRef, "vertical", {
    tabStop: itemsRole === "radio" ? '[aria-checked="true"]' : undefined,
  });

  // Closes once focus lands outside it, but not in a menu or listbox opened from inside it, which
  // renders in a portal of its own; focus leaving that menu for the page closes the popover too
  useEffect(() => {
    if (!open) return undefined;

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as Element;
      if (refs.floating.current?.contains(target)) return;
      if ((refs.reference.current as Element | null)?.contains(target)) return;
      // Not every dialog: another of these popovers is one, and focus moving there closes this
      if (target.closest('[role="menu"], [role="listbox"]')) return;
      setOpen(false);
    };

    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, [open, refs]);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeDelay = typeof delay === "object" ? (delay?.close ?? 200) : (delay ?? 200);

  useEffect(() => {
    if (!open) return undefined;

    const onMouseMove = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target) return;

      const triggerEl = refs.reference.current as Element | null;
      const panelEl = refs.floating.current;
      const insideTrigger = !!triggerEl && triggerEl.contains(target);
      const insidePanel = !!panelEl && panelEl.contains(target);

      if (
        insideTrigger ||
        insidePanel ||
        hasOpenNestedFloating(panelEl) ||
        hasFocusedChild(panelEl)
      ) {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        return;
      }

      if (closeTimerRef.current) return;
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        setOpen(false);
      }, closeDelay);
    };

    document.addEventListener("mousemove", onMouseMove);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [open, refs, closeDelay]);

  const renderItem = (item: DropdownItem, index: number) => {
    if ("type" in item) {
      if (item.type === "separator") {
        const key = `separator${index}`;
        return <div key={key} className="h-px my-1 mx-2.5 bg-gray-200 dark:bg-gray-800" />;
      }
      if (item.type === "title") {
        const key = `title${index}`;
        return (
          <div
            key={key}
            className="min-h-[24px] py-1.5 px-2.5 text-[12px] leading-[14px] text-document-text font-mono uppercase tracking-tight"
          >
            {item.title}
          </div>
        );
      }
      return null;
    }

    const { footer: ItemFooter, header: ItemHeader } = item;

    if ("component" in item) {
      const key = `custom${index}`;
      return (
        <Fragment key={key}>
          {ItemHeader && <ItemHeader open={open} close={close} />}
          <div>
            {typeof item.component === "function"
              ? item.component({ open, close })
              : item.component}
          </div>
          {ItemFooter && <ItemFooter open={open} close={close} />}
        </Fragment>
      );
    }

    if ("renderItem" in item) {
      const key = `render${index}`;
      return (
        <Fragment key={key}>
          {ItemHeader && <ItemHeader open={open} close={close} />}
          <div>{item.renderItem({})}</div>
          {ItemFooter && <ItemFooter open={open} close={close} />}
        </Fragment>
      );
    }

    const key = `${item.title}${index}`;
    return (
      <Fragment key={key}>
        {ItemHeader && <ItemHeader open={open} close={close} />}
        <button
          type="button"
          data-testid={item.testId}
          aria-label={item.title}
          role={item.toggle ? undefined : itemsRole}
          aria-checked={itemsRole === "radio" && !item.toggle ? !!item.active : undefined}
          aria-pressed={item.toggle ? !!item.active : undefined}
          disabled={item.disabled}
          onClick={(e) => {
            item.onClick?.(e);
            close();
          }}
          className={twMerge(
            "w-full min-h-8.5 py-1.25 px-2.5 text-sm flex items-center rounded-8 whitespace-nowrap tracking-tight",
            "sm:text-[16px]",
            !item.disabled && "hover:bg-sidebar-button-active-bg",
            item.disabled && "opacity-50",
            item.className,
          )}
        >
          {item.icon && (
            <div className="min-w-6 h-6 mr-2.5 [&>svg]:w-full [&>svg]:h-full">{item.icon}</div>
          )}
          <span className="text-left">{item.title}</span>
          {item.active && <Icon name="Check" className="w-6 h-6 ml-auto -mr-1" />}
        </button>
        {ItemFooter && <ItemFooter open={open} close={close} />}
      </Fragment>
    );
  };
  const isToggle = (item: DropdownItem) => "toggle" in item && !!item.toggle;

  const renderedChildren = typeof children === "function" ? children({ open, close }) : children;

  return (
    <>
      <div
        ref={refs.setReference}
        data-open={open || undefined}
        className={twMerge("rounded-8 data-[open]:bg-sidebar-button-hover-bg", className)}
        {...getReferenceProps({
          // Enter and Space click with `detail` 0
          onClickCapture: (event: React.MouseEvent) => {
            openedWithKeyboardRef.current = event.detail === 0;
          },
        })}
      >
        {isValidElement<Record<string, unknown>>(toggleButtonComponent)
          ? cloneElement(toggleButtonComponent, {
              "aria-expanded": open,
              "aria-haspopup": "dialog",
            })
          : toggleButtonComponent}
      </div>
      {open && (
        <FloatingPortal>
          {/* Non-modal: Tab runs from the trigger into the popover and on past it, closing it */}
          <FloatingFocusManager
            context={context}
            modal={false}
            closeOnFocusOut={false}
            initialFocus={openedWithKeyboardRef.current ? 0 : -1}
            returnFocus={openedWithKeyboardRef.current}
          >
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              role="dialog"
              aria-label={label}
              className={twMerge(
                "p-1.5 rounded-12 bg-block-bg shadow-md dark:shadow-block-inverted outline-none z-50",
                itemsClassName,
              )}
              {...getFloatingProps()}
            >
              {renderedChildren}
              {items && (
                <div ref={itemsRef}>
                  <div
                    role={itemsRole === "radio" ? "radiogroup" : undefined}
                    aria-label={itemsRole === "radio" ? label : undefined}
                  >
                    {items.filter((item) => !isToggle(item)).map(renderItem)}
                  </div>
                  {items.filter(isToggle).map(renderItem)}
                </div>
              )}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
